import { useNavigation } from "@react-navigation/native";
import { Divider } from "@ui-kitten/components";
import FilterIcon from "components/FilterIcon";
import dayjs from "dayjs";
import {
  setOriginalTickets,
  setSelectedEvent,
} from "features/slices/reservationFlowSlice";
import { EventAvailabilityDetails } from "features/types";
import { useAccount } from "hooks/useAccount";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import i18n from "plugins/localization";
import { useFetchEventAvailablitiesQuery } from "queries/ticket.queries";
import React, { useEffect, useMemo, useState } from "react";
import { ActivityIndicator, Text, View } from "react-native";
import {
  getUniqSubscriptions,
  sortVoucherSubsByUnusedVouchers,
} from "utils/subscriptionUtils";
import toast from "utils/toast";
import Filters from "./components/Filters";
import TDCDialog from "./components/TDCDialog";
import ScheduleItemList from "./ScheduleItemList";

export const NoEventsText = () => (
  <Text className="text-3xl font-bold text-gray-400 text-center">
    {i18n.t("screens.Schedule.noEligibleEventsBanner")}
  </Text>
);

const MONTH_FILTER_FORMAT = "MMM";
const DAY_FILTER_FORMAT = "ddd";

const CapturedSchedule = () => {
  const { account } = useAccount();
  const dispatch = useAppDispatch();
  const navigation: any = useNavigation();

  const [emptySchedule, setEmptySchedule] = useState(false);
  const [toggled, setToggled] = useState(false);
  const [TDCDialogVisible, setTDCDialogVisible] = useState(false);

  const { selectedSubscription, eventFilters, selectedEvent } = useAppSelector(
    (state) => state.reservationFlow
  );

  if (!account) return null;

  const {
    data: schedule = [],
    isFetching,
    refetch: fetchEventAvailablities,
  } = useFetchEventAvailablitiesQuery({
    accountId: account.id,
    planPackageId: selectedSubscription?.planPackage.id,
  });

  const planPackageOperable = selectedSubscription?.operative || false;

  const filterSchedule = (schedule: EventAvailabilityDetails[]) => {
    const filterByEventType = (item: EventAvailabilityDetails) => {
      return eventFilters.eventTypes.length === 0
        ? true
        : eventFilters.eventTypes.includes(item.eventType);
    };

    const filterByMonths = (item: EventAvailabilityDetails) => {
      if (eventFilters.months.length === 0) return true;
      const itemMonth = dayjs(item.date)
        .tz(item.timezone)
        .format(MONTH_FILTER_FORMAT);
      return eventFilters.months.includes(itemMonth);
    };

    const filterByDay = (item: EventAvailabilityDetails) => {
      const itemDay = dayjs(item.date)
        .tz(item.timezone)
        .format(DAY_FILTER_FORMAT);
      return eventFilters.days.length === 0
        ? true
        : eventFilters.days.includes(itemDay);
    };

    return schedule
      .filter(filterByEventType)
      .filter(filterByMonths)
      .filter(filterByDay);
  };

  const onScheduleItemClick = (item: EventAvailabilityDetails) => {
    dispatch(setSelectedEvent(item));
    if (
      account?.extUserDetails?.tdcUsername === undefined &&
      item.ticketProvider === "TDC"
    ) {
      setTDCDialogVisible(true);
      return;
    }
    dispatch(setOriginalTickets([]));
    navigation.navigate("Schedule", {
      screen: "SeatSelection",
      initial: false,
    });
  };

  useEffect(() => {
    async function fetchSchedule() {
      if (!selectedSubscription || account === null) return;
      const { data: response = [] } = await fetchEventAvailablities();
      setEmptySchedule(response.length === 0);
    }
    // if selectedSubscription is undefined, then it means user has no valid subscription to be picked from,
    // the user has to add a membership
    // selectedSubscription is initialized to null
    if (selectedSubscription === undefined) {
      toast.error(i18n.t("screens.Schedule.noEligibleSubscriptions"));
      return;
    }
    fetchSchedule();
  }, [selectedSubscription, account]);

  return (
    <View className="p-4 overflow-hidden">
      {selectedEvent && (
        <TDCDialog
          visible={TDCDialogVisible}
          setVisible={setTDCDialogVisible}
          cancelText="CLOSE"
          event={selectedEvent}
        />
      )}

      <View className="mb-2 flex-row items-center justify-end">
        {emptySchedule && (
          <Text className="text-xl font-bold">
            {i18n.t("screens.Schedule.noEligibleEvents")}
          </Text>
        )}
        <FilterIcon onPress={() => setToggled(!toggled)} />
      </View>
      <Divider />
      <Filters
        toggled={toggled}
        schedule={schedule}
        monthFormat={MONTH_FILTER_FORMAT}
        dayFormat={DAY_FILTER_FORMAT}
      />
      <View className="mt-2"></View>
      {emptySchedule && <NoEventsText />}
      {isFetching ? (
        <ActivityIndicator />
      ) : (
        <ScheduleItemList
          schedule={filterSchedule(schedule)}
          selectedSubscription={selectedSubscription}
          planPackageOperable={planPackageOperable}
          onScheduleItemClick={onScheduleItemClick}
        />
      )}
    </View>
  );
};

export default CapturedSchedule;
